import {
  Button,
  IconButton,
  HStack,
  Stack,
  Text,
  Image,
  useBreakpointValue,
  Box,
  Spinner,
  VStack,
  useToast,
  Progress,
  useClipboard,
  Divider,
} from "@chakra-ui/react";
import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { FiCopy, FiEdit2, FiExternalLink } from "react-icons/fi";

import { FaCloudUploadAlt, FaWarehouse } from "react-icons/fa";

import actions from "../actions";
import {
  KYDAdminEventDetail,
} from "@common/interfaces/KYDEvent";
import { Outlet, useParams, useLocation, matchPath } from "react-router-dom";
import ReactS3Uploader from "react-s3-uploader";

import { ModalType, EditModalType, showSuccessToast } from "../utils";
import "react-datepicker/dist/react-datepicker.css";

import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import customParseFormat from "dayjs/plugin/customParseFormat";
import relativeTime from "dayjs/plugin/relativeTime";

import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useDialog } from "../Common/Dialog";
import { useLoading, useProgress } from "./hooks/form";
import VenueDisplayHeader from "./VenueDisplayHeader";
import {
  DesktopSideBar,
  MobileNavBar,
} from "./EventDetail/Nav/EventDetailsNavigation";

dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(customParseFormat);
dayjs.extend(relativeTime);

const EventDetail = ({
  setAppModalType,
}: {
  setAppModalType: (state: ModalType, description: string) => void;
}) => {
  const [kydEvent, setKydEvent] = useState<KYDAdminEventDetail | null>(null);
  const { event_id } = useParams();
  const [modalType, setModalType] = useState(EditModalType.INACTIVE);
  const [dirtyText, setDirtyText] = useState<any>({});
  const [loading, updateLoading] = useLoading({
    tickettype: false,
    promocode: false,
    create_affiliates: false,
    blocking: false,
    event: false,
    metrics: false,
  });
  const [progress, updateProgress] = useProgress({
    poster: false,
  });

  const isMobile = useBreakpointValue({
    base: true,
    md: false,
  });

  const location = useLocation();

  const hideEventDetailHeaderUrlList = [
    `/events/:event_id/settings`,
    `events/:event_id/reporting`,
  ];
  const shouldHideHeader = useMemo(() => {
    const isThere = hideEventDetailHeaderUrlList.some((url) => {
      return matchPath(url, location.pathname);
    });
    return isThere;
  }, [location]);

  // TODO: think of this
  const shouldShowHeader = false;

  const { onCopy } = useClipboard(kydEvent?.preview_url);
  const toast = useToast();
  const dialog = useDialog();
  const uploadRef = useRef<HTMLInputElement>(null);

  const hasVenue = !!kydEvent?.venues?.at(0);

  const updateDirtyText = useCallback((update: any | string) => {
    if (update === "all") {
      setDirtyText({});
    } else {
      setDirtyText((l: any) => {
        const keys = Object.keys(update);
        keys.forEach((key) => {
          if (update[key] === null) {
            delete l[key];
          } else {
            l[key] = update[key];
          }
        });
        return { ...l };
      });
    }
  }, []);

  const fetchEvent = useCallback(
    async (eventId?: string, options?: any) => {
      if (!options || (options && !options.silent)) {
        updateLoading("init", true);
      }

      let _;
      try {
        if (!eventId) {
          throw Error("NO event ID provided");
        }
        _ = await actions.ADMIN.fetchEventWithSections(eventId);
        setKydEvent(_);
        dayjs.tz.setDefault(_.timezone);

        if (
          _ &&
          _.latest_update &&
          _.latest_update.status === "PENDING" &&
          !options
        ) {
          updateLoading("init", false);
          updateLoading("blocking", true);
          await pollForEventUpdateStatus();
          updateLoading("blocking", false);
        }
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading("init", false);
      return _;
    },
    [updateLoading]
  );

  useEffect(() => {
    if (event_id) {
      fetchEvent(event_id);
    }
  }, [event_id, fetchEvent]);

  const pollForEventUpdateStatus = useCallback(async () => {
    console.log("Poll Event Status");
    const searchParams = new URLSearchParams(window.location.search);
    const updateId = searchParams.get("update_id");
    const sleep = (milliseconds: number) => {
      return new Promise((resolve) => setTimeout(resolve, milliseconds));
    };

    if (updateId) {
      let attempts = 0;
      let didFind = false;

      do {
        console.log("Attempt: ", attempts);
        let serverEventUpdate: any;
        let failed = false;
        try {
          serverEventUpdate = await actions.ADMIN.fetchEventUpdate(
            event_id!,
            updateId
          );
        } catch (err) {
          console.log(err);
          failed = true;
        }

        if (
          !failed &&
          (!serverEventUpdate || serverEventUpdate.status === "COMPLETE")
        ) {
          didFind = true;
          searchParams.delete("update_id");

          const newUrl =
            window.location.protocol +
            "//" +
            window.location.host +
            window.location.pathname +
            "?" +
            searchParams.toString();
          window.history.pushState({ path: newUrl }, "", newUrl);

          if (
            serverEventUpdate &&
            serverEventUpdate.status === "COMPLETE" &&
            serverEventUpdate.error
          ) {
            console.log(
              "Found Update: ",
              JSON.stringify(serverEventUpdate, null, 2)
            );
            dialog({
              text: serverEventUpdate.error.message || serverEventUpdate.error,
            });
            return false;
          } else {
            await fetchEvent(event_id!, { silent: true });
          }
          break;
        } else {
          await sleep(3000);
          attempts += 1;
        }
      } while (attempts < 15);

      return didFind;
    }

    return false;
  }, [event_id, fetchEvent]);

  const onUpdateEvent = useCallback(
    async (key: string, override_value?: string) => {
      updateLoading("event", true);
      try {
        const value = override_value || (dirtyText[key] as string);
        const result = await actions.ADMIN.updateEvent(event_id!, key, value);
        console.log("Result: ", result);
        if (result && result.update_id) {
          const params = new URLSearchParams(window.location.search);
          params.set("update_id", result.update_id);

          const newUrl =
            window.location.protocol +
            "//" +
            window.location.host +
            window.location.pathname +
            "?" +
            params.toString();
          window.history.pushState({ path: newUrl }, "", newUrl);

          const success = await pollForEventUpdateStatus();
          if (success) {
            console.log("Updated Event");
            updateDirtyText("all");
            toast({
              title: "Updated",
              description: "Event has been updated",
              status: "success",
              duration: 5000,
              isClosable: true,
            });
          }
        } else {
          await fetchEvent(event_id!, { silent: true });
        }
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading("event", false);
      updateProgress("poster", 0);
      setModalType(EditModalType.INACTIVE);
    },
    [
      event_id,
      dirtyText,
      fetchEvent,
      updateLoading,
      pollForEventUpdateStatus,
      updateDirtyText,
      updateProgress,
      dialog,
      toast,
    ]
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack
        height="100%"
        spacing={0}
        direction={["column", "row"]}
        width={"full"}
      >
        <DesktopSideBar
          show={!isMobile}
          nav={kydEvent?.nav}
          isLoading={loading.event}
        />
        <Stack
          spacing={{ base: "8", lg: "6" }}
          transform="translate3d(0,0,0)"
          overflowY="scroll"
          width="100%"
          //style={{ "::-webkit-scrollbar": { display: "none" } }}
          height="100%"
          p={8}
        >
          {loading["init"] && (
            <VStack>
              <Spinner />
              <Text>Loading event...</Text>
            </VStack>
          )}

          {kydEvent && (
            <Stack
              spacing={10}
              maxWidth={["100%", "5xl"]}
              minWidth={["100%", "initial"]}
              width="100%"
              margin="0 auto"
            >
              {shouldShowHeader ? (
                <>
                  <Stack direction={["column", "row"]}>
                    <Stack
                      w="100%"
                      direction={{ base: "column", md: "row" }}
                      spacing={3}
                    >
                      <VStack>
                        <Image
                          fit={"contain"}
                          rounded={"lg"}
                          maxH="150px"
                          maxW={"150px"}
                          src={kydEvent.image}
                          fallbackSrc="https://content.kydlabs.com/system/plcaeholderimage.png"
                        />
                        <Button
                          isDisabled={loading.event || loading.blocking}
                          isLoading={loading.event}
                          onClick={() => {
                            if (uploadRef && uploadRef.current) {
                              uploadRef.current.click();
                            }
                          }}
                          aria-label="Upload event image"
                          variant={"outline"}
                          leftIcon={<FaCloudUploadAlt />}
                        >
                          Change Poster
                        </Button>
                        {progress.poster > 0 && (
                          <Progress
                            h="15px"
                            rounded={"lg"}
                            colorScheme={"green"}
                            value={progress.poster}
                          />
                        )}
                        {!progress.poster || progress.poster === 0 ? (
                          <Box h="15px" w="100%" />
                        ) : null}
                        <ReactS3Uploader
                          style={{ display: "none" }}
                          //@ts-ignore
                          inputRef={uploadRef}
                          getSignedUrl={(file, callback) =>
                            actions.ADMIN.createUploadUrl(file, callback)
                          }
                          accept="image/*"
                          onProgress={(p) => {
                            updateProgress("poster", p);
                          }}
                          onError={(e) => {
                            updateLoading("event", false);
                            dialog({ text: e });
                          }}
                          preprocess={(f, next) => {
                            updateLoading("event", true);
                            next(f);
                          }}
                          onFinish={(result) => {
                            onUpdateEvent("poster", result.fileKey);
                          }}
                          uploadRequestHeaders={(file) => ({
                            "Content-Type": file.type,
                            "x-amz-acl": "private",
                          })}
                          contentDisposition="auto"
                        />
                      </VStack>
                      <Stack w="100%" spacing={4}>
                        <Stack
                          w="100%"
                          justify={"space-between"}
                          direction={{ base: "column", md: "row" }}
                        >
                          <Stack spacing={0} justifyContent={"center"}>
                            <HStack>
                              <Text fontSize={"xl"} fontWeight="bold">
                                {kydEvent.name}
                              </Text>{" "}
                              <IconButton
                                isDisabled={loading["blocking"]}
                                onClick={() => setModalType(EditModalType.NAME)}
                                icon={<FiEdit2 fontSize="1.25rem" />}
                                variant="ghost"
                                aria-label="Edit member"
                              />
                            </HStack>
                            <VenueDisplayHeader
                              venue={kydEvent?.venues?.at(0)}
                            />
                            <Button
                              maxW={"180px"}
                              isDisabled={loading.event || loading.blocking}
                              isLoading={loading.event}
                              onClick={() => setModalType(EditModalType.VENUE)}
                              aria-label="Change venue"
                              variant={"outline"}
                              leftIcon={<FaWarehouse />}
                            >
                              {hasVenue ? "Change Venue" : "Set Venue"}
                            </Button>
                            <Stack spacing={0}>
                              {kydEvent.display_start_at ? (
                                <Text fontSize={"sm"}>
                                  Event start: {kydEvent.display_start_at}
                                </Text>
                              ) : null}
                              {kydEvent.display_start_at ? (
                                <Text fontSize={"sm"}>
                                  Event end: {kydEvent.display_end_at}
                                </Text>
                              ) : null}
                              {kydEvent.timezone ? (
                                <Text fontSize={"sm"}>
                                  Timezone: {kydEvent.timezone}
                                </Text>
                              ) : null}
                            </Stack>
                          </Stack>

                          <Stack>
                            {kydEvent.preview_url ? (
                              <HStack>
                                <Button
                                  variant={"outline"}
                                  onClick={() =>
                                    window.open(kydEvent.preview_url, "_blank")
                                  }
                                  rightIcon={<FiExternalLink />}
                                >
                                  Preview Event
                                </Button>
                                <IconButton
                                  variant={"outline"}
                                  onClick={() => {
                                    onCopy();
                                    showSuccessToast(
                                      toast,
                                      "Preview Link Copied"
                                    );
                                  }}
                                  icon={<FiCopy />}
                                  aria-label="Copy preview link"
                                />
                              </HStack>
                            ) : null}
                            {kydEvent.short_link ? (
                              <>
                                <Button
                                  variant={"outline"}
                                  onClick={() =>
                                    window.open(kydEvent.short_link, "_blank")
                                  }
                                  rightIcon={<FiExternalLink />}
                                >
                                  Go To Event
                                </Button>
                              </>
                            ) : null}
                          </Stack>
                        </Stack>
                      </Stack>
                    </Stack>
                  </Stack>

                  <Divider />
                </>
              ) : null}
              <MobileNavBar
                show={isMobile}
                nav={kydEvent?.nav}
                isLoading={loading.event}
              />
              <Outlet key={kydEvent?.venues?.at(0)?.id} />
            </Stack>
          )}
        </Stack>
      </Stack>
    </LocalizationProvider>
  );
};

export default EventDetail;
